import React, { useCallback, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  useBottomNavStore,
  ALL_NAV_OPTIONS,
  NEU_ITEM,
  type NavItem,
} from '../../store/bottomNavStore';

// ---------------------------------------------------------------------------
// Long-press hook
// ---------------------------------------------------------------------------
function useLongPress(callback: () => void, ms = 2000) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggered = useRef(false);

  const start = useCallback(
    (_e: React.TouchEvent | React.MouseEvent) => {
      triggered.current = false;
      timerRef.current = setTimeout(() => {
        triggered.current = true;
        // Vibrate if available
        if (navigator.vibrate) navigator.vibrate(50);
        callback();
      }, ms);
    },
    [callback, ms]
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Prevent navigation when long-press was triggered
  const shouldPreventNav = useCallback(() => triggered.current, []);

  return { onTouchStart: start, onTouchEnd: cancel, onTouchMove: cancel, onMouseDown: start, onMouseUp: cancel, onMouseLeave: cancel, shouldPreventNav };
}

// ---------------------------------------------------------------------------
// Nav Item Picker Modal
// ---------------------------------------------------------------------------
const NavItemPicker: React.FC<{
  slotIndex: number;
  currentPath: string;
  onSelect: (path: string) => void;
  onClose: () => void;
}> = ({ currentPath, onSelect, onClose }) => {
  const { slots } = useBottomNavStore();

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fade-in" onClick={onClose} />

      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up pb-safe">
        <div className="bg-white dark:bg-[#1e1e2e] rounded-t-3xl border-t border-white/50 dark:border-indigo-500/10 shadow-2xl max-h-[70vh] overflow-hidden">
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          </div>

          <div className="px-5 pb-2">
            <h3 className="text-lg font-bold text-neutral-800 dark:text-white">
              Menüpunkt ändern
            </h3>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
              Wähle aus, was hier angezeigt werden soll
            </p>
          </div>

          <div className="px-3 pb-6 overflow-y-auto max-h-[50vh]">
            <div className="grid grid-cols-3 gap-2 p-2">
              {ALL_NAV_OPTIONS.map((option) => {
                const isCurrentSlot = option.path === currentPath;
                const isUsedElsewhere = slots.includes(option.path) && !isCurrentSlot;

                return (
                  <button
                    key={option.path}
                    onClick={() => {
                      onSelect(option.path);
                      onClose();
                    }}
                    className={`
                      flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all duration-200
                      ${isCurrentSlot
                        ? 'bg-primary-500/10 border-2 border-primary-500/30 dark:border-primary-400/30'
                        : isUsedElsewhere
                        ? 'bg-neutral-100/50 dark:bg-white/5 opacity-40'
                        : 'bg-neutral-50 dark:bg-white/5 hover:bg-primary-50 dark:hover:bg-primary-500/10 active:scale-95'
                      }
                    `}
                  >
                    <span className="text-2xl">{option.emoji}</span>
                    <span className={`text-[11px] font-medium ${
                      isCurrentSlot
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-neutral-600 dark:text-neutral-300'
                    }`}>
                      {option.label}
                    </span>
                    {isCurrentSlot && (
                      <span className="text-[9px] text-primary-500 font-semibold">Aktuell</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Single Nav Button
// ---------------------------------------------------------------------------
const NavButton: React.FC<{
  item: NavItem & { highlight?: boolean };
  slotIndex?: number;
  onLongPress?: () => void;
}> = ({ item, onLongPress }) => {
  const longPress = useLongPress(() => {
    if (onLongPress) onLongPress();
  }, 2000);

  const handleClick = (e: React.MouseEvent) => {
    if (longPress.shouldPreventNav()) {
      e.preventDefault();
    }
  };

  // For customizable items, we use a div with manual navigation to prevent
  // the link from firing during long-press
  if (onLongPress) {
    return (
      <NavLink
        to={item.path}
        end={item.path === '/'}
        aria-label={item.label}
        onClick={handleClick}
        className={({ isActive }) =>
          `flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-2xl
           transition-all duration-200 min-w-[56px] relative select-none
          ${isActive ? 'text-primary-600' : 'text-neutral-400 dark:text-neutral-500'}`
        }
        {...longPress}
      >
        {({ isActive }) => (
          <>
            <div className="relative">
              <span className={`text-lg transition-transform duration-200 block ${isActive ? 'scale-110' : ''}`}>
                {item.emoji}
              </span>
              {isActive && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
              )}
            </div>
            <span className={`text-[10px] font-medium ${isActive ? 'text-primary-600 font-semibold' : ''}`}>
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    );
  }

  // Fixed "Neu" button — no long-press
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      aria-label={item.label}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-2xl
         transition-all duration-200 min-w-[56px] relative
        ${(item as any).highlight && !isActive ? '' : isActive ? 'text-primary-600' : 'text-neutral-400 dark:text-neutral-500'}`
      }
    >
      {({ isActive }) => (
        <>
          {(item as any).highlight && !isActive ? (
            <div className="gradient-primary -mt-5 w-12 h-12 rounded-full flex items-center justify-center shadow-glow-primary">
              <span className="text-xl text-white">{item.emoji}</span>
            </div>
          ) : (
            <div className="relative">
              <span className={`text-lg transition-transform duration-200 block ${isActive ? 'scale-110' : ''}`}>
                {item.emoji}
              </span>
              {isActive && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
              )}
            </div>
          )}
          <span className={`text-[10px] font-medium ${
            (item as any).highlight && !isActive ? 'text-primary-500 mt-0.5' : ''
          } ${isActive ? 'text-primary-600 font-semibold' : ''}`}>
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  );
};

// ---------------------------------------------------------------------------
// Bottom Nav
// ---------------------------------------------------------------------------
export const BottomNav: React.FC = () => {
  const { slots, setSlot } = useBottomNavStore();
  const [pickerState, setPickerState] = useState<{ slotIndex: number; currentPath: string } | null>(null);

  // Resolve slot paths to NavItem objects
  const resolveSlot = (path: string): NavItem => {
    return ALL_NAV_OPTIONS.find((o) => o.path === path) || ALL_NAV_OPTIONS[0];
  };

  // Build nav items: [slot0, slot1, NEU, slot2, slot3]
  const items = [
    { item: resolveSlot(slots[0]), slotIndex: 0 },
    { item: resolveSlot(slots[1]), slotIndex: 1 },
    { item: NEU_ITEM, slotIndex: -1 }, // fixed
    { item: resolveSlot(slots[2]), slotIndex: 2 },
    { item: resolveSlot(slots[3]), slotIndex: 3 },
  ];

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-2xl border-t border-white/50 dark:border-indigo-500/10 pb-safe pl-safe pr-safe">
        <div className="flex items-center justify-around h-16 px-2">
          {items.map(({ item, slotIndex }) => (
            <NavButton
              key={slotIndex === -1 ? 'neu' : `slot-${slotIndex}`}
              item={item}
              slotIndex={slotIndex >= 0 ? slotIndex : undefined}
              onLongPress={
                slotIndex >= 0
                  ? () => setPickerState({ slotIndex, currentPath: item.path })
                  : undefined
              }
            />
          ))}
        </div>
      </nav>

      {/* Picker Modal */}
      {pickerState && (
        <NavItemPicker
          slotIndex={pickerState.slotIndex}
          currentPath={pickerState.currentPath}
          onSelect={(path) => setSlot(pickerState.slotIndex, path)}
          onClose={() => setPickerState(null)}
        />
      )}
    </>
  );
};
